import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from '../../../components/ui';
import { locationsService } from '../../../services/locationsService';
import type { SiteWithLocations, StorageLocation } from '../types';
import { LOCATION_KINDS, LOCATION_NAME_MAX_LENGTH, toLocationInput, validateLocationForm, type LocationForm } from '../utils/structureForms';

interface StorageLocationFormModalProps {
  show: boolean;
  site: SiteWithLocations;
  /** The location to rename or retype, or null to add one. */
  location: StorageLocation | null;
  onHide: () => void;
  onSaved: (message: string) => void;
}

const Required: React.FC = () => (
  <span className="tw-text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

/**
 * Adds a storage location to a site (a shed, a stockpile, a tank), or renames or retypes one.
 * An empty location can be removed here too; one with bins keeps its place in the history.
 */
const StorageLocationFormModal: React.FC<StorageLocationFormModalProps> = ({ show, site, location, onHide, onSaved }) => {
  const [form, setForm] = useState<LocationForm>({ name: '', kind: '' });
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setForm(location ? { name: location.name, kind: location.kind } : { name: '', kind: 'STOCKPILE' });
    setAttempted(false);
    setBusy(null);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const errors = validateLocationForm(form, site.locations, site.name, location?.id ?? null);
  const shown = attempted ? errors : {};
  const removeReason = location && location.bins.length > 0 ? `${location.name} still has bins; remove them first` : null;

  const save = async () => {
    setAttempted(true);
    if (Object.keys(errors).length) return;
    setBusy('save');
    setSaveError(null);
    try {
      const input = toLocationInput(form);
      if (location) await locationsService.updateStorageLocation(site.id, location.id, input);
      else await locationsService.createStorageLocation(site.id, input);
      onSaved(location ? `Saved ${input.name}.` : `Added ${input.name}. Add its bins next.`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save the location');
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!location) return;
    setBusy('remove');
    setSaveError(null);
    try {
      await locationsService.deleteStorageLocation(site.id, location.id);
      onSaved(`Removed ${location.name}.`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to remove the location');
      setBusy(null);
    }
  };

  return (
    <Modal show={show} onHide={busy ? () => undefined : onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{location ? `Edit ${location.name}` : 'Add a storage location'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-space-y-4">
        <p className="tw-mb-0 tw-text-sm tw-text-content-secondary">
          A named place at {site.name} that holds bins: a shed, a stockpile, a tank.
        </p>
        {saveError ? <Alert variant="danger" className="tw-mb-0">{saveError}</Alert> : null}
        <Form.Group controlId="location-form-name">
          <Form.Label>
            Name
            <Required />
          </Form.Label>
          <Form.Control
            autoFocus
            value={form.name}
            maxLength={LOCATION_NAME_MAX_LENGTH}
            placeholder="e.g. A-4 Salt shed"
            isInvalid={!!shown.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setForm((current) => ({ ...current, name: event.target.value }));
              setSaveError(null);
            }}
          />
          {shown.name ? <Form.Control.Feedback type="invalid">{shown.name}</Form.Control.Feedback> : null}
        </Form.Group>
        <Form.Group controlId="location-form-kind">
          <Form.Label>
            Kind
            <Required />
          </Form.Label>
          <Form.Select
            value={form.kind}
            isInvalid={!!shown.kind}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setForm((current) => ({ ...current, kind: event.target.value }))}
          >
            {LOCATION_KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {location ? (
          <Button
            variant="outline-danger"
            className="tw-mr-auto"
            onClick={() => void remove()}
            disabled={busy !== null || removeReason !== null}
            disabledReason={busy ? undefined : removeReason ?? undefined}
          >
            {busy === 'remove' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash" aria-hidden="true" />}
            Remove location
          </Button>
        ) : null}
        <Button variant="outline-secondary" onClick={onHide} disabled={busy !== null}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void save()} disabled={busy !== null}>
          {busy === 'save' ? <Spinner animation="border" size="sm" /> : null}
          {location ? 'Save location' : 'Add location'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StorageLocationFormModal;
